import type { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import type { Observable } from 'rxjs';
import { catchError, of, timeout } from 'rxjs';

import { EnvironmentService } from '@app/core/environment-service';
import { IS_SERVER_TOKEN } from '@app/shared/utils';

const CACHE_SERVER = 'http://localhost:3000';
const CACHE_LOCALES_SERVER = 'http://localhost:3000/api/locales';

@Injectable()
export class ErrorSsgInterceptor implements HttpInterceptor {
  readonly #SSR_REQUEST_TIMEOUT_MS = 5000;
  readonly #environmentService = inject(EnvironmentService);
  readonly #isServer = inject<boolean>(IS_SERVER_TOKEN);

  readonly #environment = this.#environmentService.getEnvironment();
  readonly #GATEWAY_URL = this.#environment.GATEWAY_URL;
  readonly #LOCALIZATION_URL = this.#environment.LOCALIZATION_URL;

  #handleRequestWithSsrFallback<T>(request: HttpRequest<T>, next: HttpHandler): Observable<HttpEvent<T>> {
    return next.handle(request).pipe(
      timeout(this.#SSR_REQUEST_TIMEOUT_MS),
      catchError(() =>
        of(
          new HttpResponse<T>({
            status: 200,
            body: null,
          }),
        ),
      ),
    );
  }

  intercept<T>(request: HttpRequest<T>, next: HttpHandler): Observable<HttpEvent<T>> {
    const url = request.url;
    const isAssets = url.includes('assets');

    if (!this.#isServer || isAssets) {
      return next.handle(request);
    }

    if (url.includes(this.#GATEWAY_URL)) {
      const changedUrl = url.replace(this.#GATEWAY_URL, CACHE_SERVER);

      return this.#handleRequestWithSsrFallback(
        request.clone({
          url: changedUrl,
        }),
        next,
      );
    }

    if (url.includes(this.#LOCALIZATION_URL)) {
      const changedUrl = url.replace(this.#LOCALIZATION_URL, CACHE_LOCALES_SERVER);

      return this.#handleRequestWithSsrFallback(
        request.clone({
          url: changedUrl,
        }),
        next,
      );
    }

    return this.#handleRequestWithSsrFallback(request, next);
  }
}
